import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'

import type { AppError } from '@jotta/types/AppError'
import { DefinitionListFromObject } from '@jotta/ui/DefinitionList'
import { useBrandTheme } from '@jotta/ui/useBrandTheme'

import { BaseErrorPage } from './BaseErrorPage'

export function MutationStatusAlert({
  successText,
  errorText,
  mutation,
}: {
  successText: string
  errorText: string
  mutation: UseMutationResult<any, any, any, any>
}) {
  switch (mutation.status) {
    case 'success':
      return <div className="alert alert-good">{successText}</div>
    case 'error':
      return <div className="alert alert-danger">{errorText}</div>
  }
}

export function ErrorPageError({
  error,
  resetError,
  title,
}: {
  error: AppError
  resetError?: () => void
  title?: React.ReactNode
}) {
  const { brandName } = useBrandTheme()
  const errorJson = error.toJSON()
  const copyToClipboardMutation = useMutation({
    mutationFn: (text: string) => navigator.clipboard.writeText(text),
    throwOnError: false,
  })
  return (
    <BaseErrorPage reportError={error} title={title}>
      <p>
        <Trans>
          Please try the wanted action again, if the error persists please{' '}
          <a href="/support">contact {brandName} support</a>
        </Trans>
      </p>
      <details className="mb-16">
        <summary
          sx={{
            pl: 0,
            pb: 2,
            ml: 4,
            listStylePosition: 'outside',
          }}
        >
          <Trans>Technical details</Trans>
        </summary>
        <DefinitionListFromObject value={errorJson} />
        <div className="my-4 flex flex-col items-start gap-2">
          <button
            className="btn btn-primary"
            onClick={event => {
              event.preventDefault()
              copyToClipboardMutation.mutate(
                JSON.stringify({
                  ...errorJson,
                  stack: error.stack,
                }),
              )
            }}
          >
            <Trans>Copy to clipboard</Trans>
          </button>
          <MutationStatusAlert
            mutation={copyToClipboardMutation}
            successText={t`Copied to clipboard`}
            errorText={t`Could not copy to clipboard`}
          />
        </div>
      </details>

      {resetError && (
        <p>
          <button className="btn" onClick={resetError}>
            <Trans>Reload</Trans>
          </button>
        </p>
      )}
      <p>
        <a href="/">
          <Trans>Click here to go back to the homepage.</Trans>
        </a>
      </p>
    </BaseErrorPage>
  )
}
